import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PullToRefresh from "react-simple-pull-to-refresh";

import InfiniteScroll from "react-infinite-scroll-component";
import { useCalendar } from "components/Calendar";
import { ContainerValue, ContainerView, CorrectPullRefresh, DialogWrapper, PullGrid, } from "components/containers";
import { Button, TextPlain, TitleH1, TitleH2, } from "components/generic";
import { InputLabel, useInput } from "components/Input";
import Select, { useSelect } from "components/Select";
import { DateRange } from "components/InputCalendar";
import { MenuLeftSide } from "components/MenuLeftSide";
import Modal from "components/Modal";
import { ArrowDown } from "components/PopupMenu/styles";
import { useContextHospi } from "context/ContextHospi";
import { IHousesStudentStripe, IPropsFC, IPropsFCOnlyPlaceholder } from "interfaces/intarfaces";
import photo_default from "assets/images/house.jpg";

import { CardContainer, DescriptionSection, EmptyRoomsSection, MainWrapper, SearchContainer, } from "./styles";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import { RoomCard } from "./RoomCard";
import { API_MATCHES, API_STUDENT, API_USERS, FilterRooms, } from "utils/constants";
import { UserAPI } from "API/userApi";

import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import { UnauthorizedUser } from "pages/ProfileUser/SimpleComponents";
import { getAllCities, getProvince } from "utils/fetchMapInfo";
import { getDateOrNone, isoDate } from "utils/helpers";
import HowItWork from "components/HowItWork";
import { StudentAPI } from "API/studentApi";
import SearchSelect, { ISearchSelect } from "components/SearchSelect";

type FiltersName = typeof FilterRooms[number];

export const StudentRooms: React.FC = () => {
  const [showModal, setShowModal] = useState<number>(0);
  const { userProfile, updateProfile } = useContextHospi();
  const [selectedFilter, setSelectedFilter] = useState<FiltersName>(
    FilterRooms[0]
  );
  const prevFilter = useRef(FilterRooms[0]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [rooms, setRooms] = useState<IHousesStudentStripe[]>([]);
  const [stepFetch, setStepFetch] = useState<string>("");
  const [loading, setLoading] = useState("");
  const [disabledChangeFilter, setDisabledChangeFilter] = useState(false);
  const [priceFlag, setPriceFlag] = useState<boolean>(false)
  const [errorText, setErrorText] = useState<string>('')
  const [booleanLoading, setBooleanLoading] = useState<boolean>(true)

  const useIsInViewport = () => {
    const elementRef = useRef<any>(null)
    const [isInViewPort, setIsInViewPort] = useState(false)

    useEffect(() => {
      const observer = new IntersectionObserver((entries) => {
        const isInViewPort = !!entries[0]?.isIntersecting

        setIsInViewPort(isInViewPort)
      })
      if (elementRef.current) {
        //@ts-ignore
        observer.observe(elementRef.current)
      }

      return () => observer.disconnect()
    }, [])

    return { elementRef, isInViewPort }
  }

  const { elementRef: viewportRef, isInViewPort } = useIsInViewport()

  const { t } = useTranslation("student");

  const node = useRef(null);
  useOnClickOutside(node, () => setIsOpen(false));

  // Errors block
  const [response401, setResponse401] = useState(false);

  const titlePholder = (part: string, variable: string): IPropsFC => {
    return {
      title: (part && variable) ? t(`${part}.form.${variable}.title`) : '',
      placeholder: t(`${part}.form.${variable}.placeholder`),
      w: "initial",
      grow: "1",
    };
  };

  const titlePholderOnlyPlaceholder = (part: string, variable: string): IPropsFCOnlyPlaceholder => {
    return {
      placeholder: t(`${part}.form.${variable}.placeholder`),
      w: "initial",
      grow: "1",
    };
  };

  // Search line
  const moveInDate = useCalendar({
    value: getDateOrNone(userProfile?.preferred_move_in),
  });
  const moveOutDate = useCalendar({
    value: getDateOrNone(userProfile?.preferred_move_out),
  });
  const budget = useInput(userProfile?.max_budget ?? 0);
  const cityResidence = {
    ...useSelect(userProfile?.city_of_residence?.city ?? ""),
    required: true,
  };

  const sortBy = (
    key: FiltersName,
    data: IHousesStudentStripe[]
  ): IHousesStudentStripe[] => {
    if (data.length === 0) return data;
    if (key === "highLow")
      return data.sort((a, b) => (b.price && a.price ? b.price - a.price : 0));
    else if (key === "lowHigh")
      return data.sort((a, b) => (b.price && a.price ? a.price - b.price : 0));
    else if (key === "newest")
      return data.sort((a, b) => {
        if (a.published_at !== undefined && b.published_at !== undefined) {
          if (b?.published_at > a.published_at) return -1;
          if (b?.published_at < a.published_at) return 1;
        }
        return 0;
      });
    else
      return data.sort((a, b) => {
        if (a.is_favorited !== undefined && b.is_favorited !== undefined) {
          return Number(b.is_favorited) - Number(a.is_favorited);
        }
        return 0;
      });
  };

  useEffect(() => {
    setDisabledChangeFilter(!(Number(budget.value) > 0));
  }, [budget]);

  const changeStudentProfile = () => {
    setLoading(t("textLoading.1"));
    UserAPI.update(API_USERS + `student/${userProfile?.user_id}`, {
      preferred_move_in: isoDate(moveInDate.value),
      preferred_move_out: isoDate(moveOutDate.value),
      max_budget: (budget.value || 1) as number,
      city_of_residence: {
        country: userProfile?.city_of_residence?.country ?? "",
        province: getProvince(cityResidence.value as string),
        city: (cityResidence.value as string) ?? "",
      },
      user_type: "student",
    })
      .then((response) => {
        if (response.hasOwnProperty("user_id")) {
          setStepFetch("");
          updateProfile(response);
          setRooms([]);
          fetchMoreData(true);
        }
      })
      .finally(() => setLoading(""));
  };


  // FETCH data from server (if use pagination)
  const fetchMoreData = (changeFilter: boolean = false) => {
    const orderLine: { [prop: string]: string } = {
      newest: "created_at",
      liked: prevFilter.current === "newest" ? "created_at" : "price", // if changed filter on liked and pagination not ended
      lowHigh: "price",
      highLow: "price",
    };

    const student_query = [
      `available?user_id=${userProfile?.user_id}`,
      `&order_by=${orderLine[selectedFilter]}`,
      `&ascending=${selectedFilter === "lowHigh"}`,
      changeFilter
        ? ``
        : `${!!stepFetch.length ? `&next_batch_token=${stepFetch}` : ""}`,
    ].join("");

    setLoading(t("textLoading.2"));
    setBooleanLoading(true)
    StudentAPI.get(API_STUDENT + student_query)
      .then((response) => {
        let listHouses: IHousesStudentStripe[] = [];
        if (response.hasOwnProperty("items")) {
          listHouses = response.items.map((item: IHousesStudentStripe) => ({
            ...item,
            photos: [
              item.photos && item.photos.length
                ? item.photos[0]
                : photo_default,
            ],
            is_favorited: !!item?.is_favorited,
          }));
          setStepFetch(response.next_batch_token ?? "");

          const newList = changeFilter
            ? listHouses
            : [
              ...rooms,
              ...listHouses.filter(
                (item) =>
                  !rooms.map((room) => room.house_id).includes(item.house_id)
              ),
            ];

          const sortedData = sortBy(selectedFilter, newList);
          setRooms(sortedData);
          setBooleanLoading(false)
        } else {
          setRooms([]);
          response.hasOwnProperty("status") &&
            setResponse401(response.status === 401);
          setBooleanLoading(false)
        }

        setLoading("");
      })
      .catch(() => {
        setRooms([]);
        setLoading("");
      });
  };

  useEffect(() => {
    setIsOpen(false);
    //  Sorting items off-line when full sample
    if ((rooms.length && !stepFetch.length) || selectedFilter === "liked") {
      setRooms(sortBy(selectedFilter, rooms));
    }
    else {
      setStepFetch("");
      fetchMoreData(true);
    }
    // eslint-disable-next-line
  }, [selectedFilter]);

  const deleteRoom = async (id: string, host_id: string) => {
    setLoading(t("textLoading.3"));
    const response = await StudentAPI.update(API_MATCHES + "dislike", {
      student_id: userProfile?.user_id ?? "",
      host_id: host_id,
      house_id: id,
    });
    setLoading("");
    if (response.hasOwnProperty("success") && response.success)
      setRooms((prev) => prev.filter((item) => item.house_id !== id));
  };


  const changeLiked = async (
    id: string,
    host_id: string,
    is_liked: boolean
  ) => {
    setLoading(t("textLoading.3"));
    const response = await StudentAPI.update(
      API_MATCHES + (is_liked ? `unlike` : `like`),
      {
        student_id: userProfile?.user_id ?? "",
        host_id: host_id,
        house_id: id,
      }
    );
    setLoading("");
    if (response.hasOwnProperty("success") && response.success) {
      const newList = rooms.map((item) =>
        item.house_id === id
          ? { ...item, is_favorited: !item.is_favorited }
          : item
      );
      setRooms(
        selectedFilter === "liked" ? sortBy(selectedFilter, newList) : newList
      );
    } else {
      setErrorText(t(`myRooms.warning.${is_liked ? 'alreadyDisliked' : 'alreadyLiked'}`))
      setShowModal(2);
    }
  };

  useEffect(() => {
    (isInViewPort && !!rooms.length && !!stepFetch.length) && fetchMoreData()
  }, [isInViewPort])

  useEffect(() => {
    changeStudentProfile()
  }, [cityResidence.value, moveInDate.value, moveOutDate.value])

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  return (
    <ContainerView>
      <MenuLeftSide />

      <MainWrapper>
        {(!!loading.length || booleanLoading) && (
          <LoaderCustom size={5} color={theme.colors.bgBody} text={loading} />
        )}
        <CorrectPullRefresh>
          <PullToRefresh onRefresh={async () => fetchMoreData(true)}>
            <PullGrid>
              <ContainerValue className="rooms-filter">
                <SearchContainer>
                  <Select
                    {...cityResidence}
                    {...titlePholder("myRooms", "")}
                    items={getAllCities(
                      userProfile?.city_of_residence?.country
                    )}
                    id="whereToGo"
                  />
                  <div style={{ display: 'flex', minWidth: '50%' }}>
                    <DateRange
                      isIcon={false}
                      rightBorder={moveOutDate.value}
                      {...moveInDate}
                      {...titlePholder("myRooms", "")}
                      id="moveInDate"
                      isClose
                    />
                    <DateRange
                      isIcon={false}
                      leftBorder={moveInDate.value}
                      {...moveOutDate}
                      {...titlePholder("myRooms", "")}
                      id="moveOutDate"
                      isClose
                    />
                  </div>
                  <div onBlur={() => {
                    priceFlag && changeStudentProfile()
                    setPriceFlag(false)
                  }} onChange={() => !priceFlag && setPriceFlag(true)}>
                    <InputLabel
                      {...budget}
                      prefix="€"
                      type="number"
                      min={1}
                      required
                      {...titlePholderOnlyPlaceholder("myRooms", "maxBudgetTitle")}
                      id="budget"
                    />
                  </div>
                </SearchContainer>
              </ContainerValue>

              <DescriptionSection>
                <TitleH1 m="0 0 8px 0">{t("myRooms.pageTitle")}</TitleH1>

                {/* Available if no rooms */}
                {(!rooms.length && !booleanLoading) ? (
                  <EmptyRoomsSection>
                    <TitleH2>{t("myRooms.emptyRoomTitle")}</TitleH2>
                    {/* <Text18Size>{t("myRooms.emptyRoomDescription")}</Text18Size> */}
                  </EmptyRoomsSection>
                ) : null}

                {/* Description and filter is only available if there any rooms */}
                {rooms.length > 0 && (
                  <>
                    <HowItWork
                      title={t("butHowItWork")}
                      items={[t("myRooms.pageDescription")]}
                    />

                    <div className="filter" ref={node}>
                      <div onClick={() => setIsOpen(!isOpen)}>
                        {t("myRooms.filter.filterTitle")}:{" "}
                        <span>{t(`myRooms.filter.${selectedFilter}`)}</span>{" "}
                        <ArrowDown size="2em" up={isOpen} />
                      </div>

                      {isOpen ? (
                        <div className="filterList">
                          {FilterRooms.map((item) => (
                            <div
                              className={`listItem ${item === selectedFilter ? "selected" : ""
                                }`}
                              onClick={() => {
                                prevFilter.current = selectedFilter;
                                setSelectedFilter(item);
                              }}
                              key={item}
                            >
                              {t("myRooms.filter." + item)}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
                {/* Rooms are only available if there any rooms */}
                {!!rooms.length ? (
                  <InfiniteScroll
                    pullDownToRefreshThreshold={1200}
                    dataLength={rooms.length}
                    next={fetchMoreData}
                    hasMore={!!stepFetch.length}
                    loader={
                      <div>
                        {/*<LoaderCustom isModal={false} text={t("textLoading.2")} />*/}
                      </div>
                    }
                    scrollableTarget="hospi-main"
                    style={{ overflow: "hidden" }}
                  >
                    <CardContainer>
                      {rooms.map((res) => (
                        <RoomCard
                          key={res.house_id}
                          {...res}
                          deleteRoom={deleteRoom}
                          changeLiked={changeLiked}
                        />
                      ))}
                    </CardContainer>
                  </InfiniteScroll>
                ) : null}
              </DescriptionSection>
              <div ref={viewportRef} style={{ opacity: 0 }}>LOADER</div>
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>

      {!!showModal && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          <DialogWrapper style={{ width: "90vw", maxWidth: "492px" }}>
            {showModal === 2 && (
              <>
                <TitleH1>{t("myRooms.warning.title")}</TitleH1>
                <TextPlain>{errorText}</TextPlain>
                <div className="buttons">
                  <span />
                  <Button main onClick={() => {
                    setShowModal(0)
                    fetchMoreData(true)
                  }}>
                    {"  "}
                    {t("myRooms.warning.butOk")}
                    {"  "}
                  </Button>
                </div>
              </>
            )}
          </DialogWrapper>
        </Modal>
      )}
    </ContainerView>
  );
};
